/**
 * domains/attribution/attributionSnapshot.ts — MER Algo Footprints v8.2.0
 *
 * Factory: StateTransitionResult → AttributionSnapshot
 *
 * Consumes the output of evaluateStateTransition() and produces a clean,
 * self-contained snapshot suitable for persistence and later evaluation.
 * No engine logic lives here — this is purely a data transformation.
 */

import type { StateTransitionResult } from '../market/engines/stateTransition/types';
import type {
  AttributionSnapshot,
  SnapshotKeyLevels,
  SnapshotRegimeContext,
  SnapshotGammaContext,
} from './types';

// ─── ID generation ────────────────────────────────────────────────────────────

function generateSnapshotId(symbol: string, emittedAt: number): string {
  const rand = Math.random().toString(36).slice(2, 8);
  return `${symbol}-${emittedAt}-${rand}`;
}

// ─── Factory ──────────────────────────────────────────────────────────────────

export interface SnapshotFactoryInput {
  symbol:          string;
  timeframe:       string;
  emittedAt?:      number;          // defaults to Date.now()
  entryPrice:      number;          // close of most recent bar
  stateResult:     StateTransitionResult;
  keyLevels?:      SnapshotKeyLevels;
  regimeContext?:  SnapshotRegimeContext;
  gammaContext?:   SnapshotGammaContext;
}

/**
 * Build an AttributionSnapshot from a StateTransitionResult.
 * This is the only way snapshots should be created — ensures all fields
 * are sourced from the real engine output.
 */
export function buildAttributionSnapshot(input: SnapshotFactoryInput): AttributionSnapshot {
  const emittedAt = input.emittedAt ?? Date.now();

  return {
    snapshotId:      generateSnapshotId(input.symbol, emittedAt),
    symbol:          input.symbol,
    timeframe:       input.timeframe,
    emittedAt,

    state:           input.stateResult.state,
    actionability:   input.stateResult.actionability,
    directionalBias: input.stateResult.directionalBias,
    confidence:      input.stateResult.confidence,
    setupQuality:    input.stateResult.setupQuality,
    contextFlags:    [...input.stateResult.contextFlags],

    triggers:        { ...input.stateResult.triggers },
    invalidation:    [...input.stateResult.invalidation],

    entryPrice:      input.entryPrice,
    keyLevels:       input.keyLevels ?? {},

    regimeContext:   input.regimeContext,
    gammaContext:    input.gammaContext,

    engineVersion:   input.stateResult.engineVersion,
  };
}

/**
 * Validate a snapshot has minimum fields required for evaluation.
 * Returns array of validation errors (empty = valid).
 */
export function validateSnapshot(snap: AttributionSnapshot): string[] {
  const errors: string[] = [];

  if (!snap.snapshotId)            errors.push('snapshotId is required');
  if (!snap.symbol)                errors.push('symbol is required');
  if (!snap.state)                 errors.push('state is required');
  if (snap.entryPrice <= 0)        errors.push('entryPrice must be positive');
  if (snap.confidence < 0 || snap.confidence > 100)
    errors.push('confidence must be 0–100');
  if (snap.setupQuality < 0 || snap.setupQuality > 100)
    errors.push('setupQuality must be 0–100');

  return errors;
}
